/**
 * api.js — lib/api.js
 *
 * All HTTP calls to the Cloudflare Worker API.
 * Every request (except /login) includes 'x-session-token' header.
 * A 401 response clears the token and reloads the page → back to login.
 *
 * BASE switches automatically:
 *   dev  → /api  (Vite proxies to Worker on :8787)
 *   prod → https://options-dashboard-api.ladiida.workers.dev/api
 */

export const BASE = import.meta.env.PROD
  ? 'https://options-dashboard-api.ladiida.workers.dev/api'
  : '/api';

// ── Session token ─────────────────────────────────────────────────────────────
export const getToken   = ()  => localStorage.getItem('od:token') || '';
export const setToken   = t   => localStorage.setItem('od:token', t);
export const clearToken = ()  => localStorage.removeItem('od:token');
export const isLoggedIn = ()  => !!getToken();

function authHeaders() {
  return {
    'Content-Type':    'application/json',
    'x-session-token': getToken(),
  };
}

/**
 * Authenticated fetch wrapper.
 * Throws with a descriptive message on HTTP errors.
 * On 401, clears token and reloads (boots back to login).
 */
async function apiFetch(path, opts = {}) {
  const url = `${BASE}${path}`;
  const r = await fetch(url, {
    ...opts,
    headers: { ...authHeaders(), ...(opts.headers || {}) },
  });

  if (r.status === 401) {
    clearToken();
    window.location.reload();
    throw new Error('Session expired — please log in again');
  }

  if (!r.ok) {
    // Try to get a JSON error message, fall back to status text
    let msg = `${r.status} ${r.statusText}`;
    try {
      const d = await r.json();
      if (d?.error) msg = `${r.status}: ${typeof d.error === 'object' ? d.error.message : d.error}`;
    } catch {}
    throw new Error(msg);
  }

  return r;
}

// ── Auth ──────────────────────────────────────────────────────────────────────
export async function login(username, password) {
  // Login does NOT use apiFetch — no token yet
  const r = await fetch(`${BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || `Login failed (${r.status})`);
  // Store lastLoginAt (the previous session's login time, returned by the worker)
  if (d.lastLoginAt) localStorage.setItem('od:lastLoginAt', d.lastLoginAt);
  return { token: d.token, username: d.username || username, role: d.role || 'guest' };
}

// ── Market data ───────────────────────────────────────────────────────────────
export async function fetchQuotes(symbols) {
  const r = await apiFetch(`/quote?symbols=${encodeURIComponent(symbols.join(','))}`);
  return r.json();
}

export async function fetchChart(symbol, range = '1y', interval = '1d') {
  const r = await apiFetch(`/chart?symbol=${encodeURIComponent(symbol)}&range=${range}&interval=${interval}`);
  return r.json();
}

// ── Watchlist ─────────────────────────────────────────────────────────────────
export async function getWatchlist(userId) {
  const r = await apiFetch(`/watchlist?userId=${encodeURIComponent(userId)}`);
  return r.json();
}

export async function putWatchlist(userId, symbols) {
  const r = await apiFetch(`/watchlist?userId=${encodeURIComponent(userId)}`, {
    method: 'PUT',
    body: JSON.stringify({ symbols }),
  });
  return r.json();
}

// ── Haiku usage cost ──────────────────────────────────────────────────────────
export async function fetchUsage() {
  const r = await apiFetch('/usage');
  return r.json();
}

// ── Stable per-device ID ──────────────────────────────────────────────────────
// Used as the watchlist namespace key in KV.
// Swap for an OAuth-derived ID later for true multi-device sync.
export function getUserId() {
  let id = localStorage.getItem('od:userId');
  if (!id) {
    id = 'u_' + crypto.randomUUID();
    localStorage.setItem('od:userId', id);
  }
  return id;
}

// ── Session metadata ──────────────────────────────────────────────────────────
export const getUsername    = ()  => localStorage.getItem('od:username')    || '';
export const getRole        = ()  => localStorage.getItem('od:role')        || 'guest';
export const getLastLoginAt = ()  => localStorage.getItem('od:lastLoginAt') || null;
export const isAdmin        = ()  => getRole() === 'admin';

export function setSession(token, username, role) {
  setToken(token);
  localStorage.setItem('od:username', username);
  localStorage.setItem('od:role',     role);
}

export function clearSession() {
  clearToken();
  localStorage.removeItem('od:username');
  localStorage.removeItem('od:role');
}

// ── Admin: account management ─────────────────────────────────────────────────
export async function listAccounts() {
  const r = await apiFetch('/admin/accounts');
  return r.json();
}
export async function createAccount(username, password) {
  const r = await apiFetch('/admin/accounts', { method: 'POST', body: JSON.stringify({ username, password }) });
  return r.json();
}
export async function updateAccount(username, newUsername, newPassword) {
  const r = await apiFetch('/admin/accounts', { method: 'PUT', body: JSON.stringify({ username, newUsername, newPassword }) });
  return r.json();
}
export async function deleteAccount(username) {
  const r = await apiFetch('/admin/accounts', { method: 'DELETE', body: JSON.stringify({ username }) });
  return r.json();
}

// ── Model preference ──────────────────────────────────────────────────────────
export const MODEL_OPTIONS = [
  { key: 'haiku',  label: 'Haiku 4.5',   note: '~S$0.009/sync · fast'  },
  { key: 'sonnet', label: 'Sonnet 4',    note: '~S$0.035/sync · sharp'  },
];
export const getModelPref = ()    => localStorage.getItem('od:model') || 'haiku';
export const setModelPref = (m)   => localStorage.setItem('od:model', m);

// ── Trade Journal ─────────────────────────────────────────────────────────────
export async function journalGetTrades() {
  const r = await apiFetch('/journal/trades');
  return r.json();
}
export async function journalPutTrades(trades) {
  const r = await apiFetch('/journal/trades', { method: 'PUT', body: JSON.stringify({ trades }) });
  return r.json();
}
export async function journalGetRecords() {
  const r = await apiFetch('/journal/records');
  return r.json();
}
export async function journalPutRecords(records) {
  const r = await apiFetch('/journal/records', { method: 'PUT', body: JSON.stringify({ records }) });
  return r.json();
}
export async function journalGetRegimes() {
  const r = await apiFetch('/journal/regimes');
  return r.json();
}
export async function journalPutRegimes(tags) {
  const r = await apiFetch('/journal/regimes', { method: 'PUT', body: JSON.stringify({ tags }) });
  return r.json();
}
export async function journalGetFilter() {
  const r = await apiFetch('/journal/filter');
  return r.json();
}
export async function journalPutFilter(filter) {
  const r = await apiFetch('/journal/filter', { method: 'PUT', body: JSON.stringify(filter) });
  return r.json();
}

// ── Daily regime (saved at market close by alert engine) ──────────────────────
export async function regimeDailyGet(date) {
  const q = date ? `?date=${encodeURIComponent(date)}` : '';
  const r = await apiFetch(`/regime/daily${q}`);
  return r.json();  // returns { [date]: { regime, label, confidence, color, trendScore } } or single entry
}
export async function regimeDailyPut(date, data) {
  const r = await apiFetch('/regime/daily', { method: 'PUT', body: JSON.stringify({ date, ...data }) });
  return r.json();
}
export async function dayPlanGet() {
  const r = await apiFetch('/dayplan');
  return r.json();
}
export async function dayPlanPut(plan) {
  const r = await apiFetch('/dayplan', { method: 'PUT', body: JSON.stringify(plan) });
  return r.json();
}

// ── Strategy (persistent trading rules / framework) ───────────────────────────
export async function strategyGet() {
  const r = await apiFetch('/strategy');
  return r.json();
}
export async function strategyPut(text) {
  const r = await apiFetch('/strategy', { method: 'PUT', body: JSON.stringify({ text }) });
  return r.json();
}

// ── Session Note (active daily note) ──────────────────────────────────────────
export async function sessionNoteGet() {
  const r = await apiFetch('/session/note');
  return r.json(); // null or { id, text, createdAt, expiresAt }
}
export async function sessionNotePut(note) {
  const r = await apiFetch('/session/note', { method: 'PUT', body: JSON.stringify(note) });
  return r.json();
}
export async function sessionNoteDelete() {
  const r = await apiFetch('/session/note', { method: 'DELETE' });
  return r.json();
}

// ── Session Log (historical session notes) ────────────────────────────────────
export async function sessionLogGet() {
  const r = await apiFetch('/session/log');
  return r.json(); // { notes: [...] }
}
export async function sessionLogPut(notes) {
  const r = await apiFetch('/session/log', { method: 'PUT', body: JSON.stringify({ notes }) });
  return r.json();
}

// ── User Settings (session plan placeholder, etc.) ────────────────────────────
export async function settingsGet() {
  const r = await apiFetch('/settings');
  return r.json();
}
export async function settingsPut(patch) {
  const r = await apiFetch('/settings', { method: 'PUT', body: JSON.stringify(patch) });
  return r.json();
}

// ── Alert Engine ──────────────────────────────────────────────────────────────

export async function alertGetSettings() {
  try {
    const r = await apiFetch('/alerts/settings');
    return r.json();
  } catch (e) {
    if (e.message?.startsWith('404')) return null;
    throw e;
  }
}

export async function alertPutSettings(settings) {
  const r = await apiFetch('/alerts/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
  return r.json();
}

export async function alertGetRingtone() {
  try {
    const r = await apiFetch('/alerts/ringtone');
    const j = await r.json();
    return j?.dataUrl ?? null;
  } catch (e) {
    if (e.message?.startsWith('404')) return null;
    throw e;
  }
}

export async function alertPutRingtone(dataUrl) {
  const r = await apiFetch('/alerts/ringtone', {
    method: 'PUT',
    body: JSON.stringify({ dataUrl }),
  });
  return r.json();
}

export async function alertDeleteRingtone() {
  const r = await apiFetch('/alerts/ringtone', { method: 'DELETE' });
  return r.json();
}

export async function alertGetLog() {
  try {
    const r = await apiFetch('/alerts/log');
    return r.json();
  } catch (e) {
    if (e.message?.startsWith('404')) return [];
    throw e;
  }
}

export async function alertAppendLog(entry) {
  const r = await apiFetch('/alerts/log', {
    method: 'POST',
    body: JSON.stringify(entry),
  });
  return r.json();
}

export async function alertClearLog() {
  const r = await apiFetch('/alerts/log', { method: 'DELETE' });
  return r.json();
}

export async function alertSendPushover(token, userKey, title, message, priority = 1) {
  const r = await apiFetch('/alerts/pushover', {
    method: 'POST',
    body: JSON.stringify({ token, userKey, title, message, priority }),
  });
  return r.json();
}

export async function alertTestPush() {
  const r = await apiFetch('/alerts/test-push', { method: 'POST' });
  return r.json();
}

// ── Per-tier alert settings ───────────────────────────────────────────────────

export async function tierGetSettings(tierId) {
  try {
    const r = await apiFetch(`/alerts/tier/${tierId}/settings`);
    return r.json();
  } catch(e) { if (e.message?.startsWith('404')) return null; throw e; }
}

export async function tierPutSettings(tierId, settings) {
  const r = await apiFetch(`/alerts/tier/${tierId}/settings`, {
    method: 'PUT', body: JSON.stringify(settings),
  });
  return r.json();
}

export async function tierGetRingtone(tierId) {
  try {
    const r = await apiFetch(`/alerts/tier/${tierId}/ringtone`);
    const j = await r.json();
    return j?.dataUrl ?? null;
  } catch(e) { if (e.message?.startsWith('404')) return null; throw e; }
}

export async function tierPutRingtone(tierId, dataUrl) {
  const r = await apiFetch(`/alerts/tier/${tierId}/ringtone`, {
    method: 'PUT', body: JSON.stringify({ dataUrl }),
  });
  return r.json();
}

export async function tierDeleteRingtone(tierId) {
  const r = await apiFetch(`/alerts/tier/${tierId}/ringtone`, { method: 'DELETE' });
  return r.json();
}

export async function tierTestPush(tierId) {
  const r = await apiFetch(`/alerts/tier/${tierId}/test-push`, { method: 'POST' });
  return r.json();
}

// ── IBKR Flex integration ─────────────────────────────────────────────────────
export async function ibkrSaveCredentials(token, queryId, confQueryId) {
  const r = await apiFetch('/ibkr/credentials', { method: 'POST', body: JSON.stringify({ token, queryId, confQueryId: confQueryId || null }) });
  return r.json();
}
export async function ibkrGetStatus() {
  const r = await apiFetch('/ibkr/credentials');
  return r.json();
}
export async function ibkrFetchReport(days = 14) {
  const r = await apiFetch(`/ibkr/fetch?days=${days}`);
  if (!r.ok) { const e = await r.json().catch(() => ({ error: `HTTP ${r.status}` })); throw new Error(e.error); }
  return r.text(); // returns raw Flex XML
}
export async function ibkrFetchOptionFlow(symbol = 'SPX') {
  const r = await apiFetch(`/ibkr/option-flow?symbol=${encodeURIComponent(symbol)}`);
  if (!r.ok) { const e = await r.json().catch(() => ({ error: `HTTP ${r.status}` })); throw new Error(e.error); }
  return r.json();
}
export async function ibkrFetchConfReport(days = 14) {
  const r = await apiFetch(`/ibkr/fetch-conf?days=${days}`);
  if (!r.ok) { const e = await r.json().catch(() => ({ error: `HTTP ${r.status}` })); throw new Error(e.error); }
  return r.text(); // returns raw Flex XML (trade confirmations)
}

// ── Journal settings (start date, etc.) ──────────────────────────────────────
export async function journalGetSettings() {
  const r = await apiFetch('/journal/settings');
  return r.json();
}
export async function journalPutSettings(data) {
  const r = await apiFetch('/journal/settings', { method: 'PUT', body: JSON.stringify(data) });
  return r.json();
}
